import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useEffect, useState, useCallback, useMemo } from "react";
import { toast } from "sonner";
import {
  Shield,
  Users,
  Building2,
  Rocket,
  CheckCircle2,
  XCircle,
  Clock,
  MessageSquare,
  Sparkles,
  RotateCcw,
  DollarSign,
  TrendingUp,
  Ban,
  Search,
  Globe,
  Wrench,
  Crown,
  UserCheck,
  LifeBuoy,
  Banknote,
} from "lucide-react";
import { DashboardShell } from "@/components/DashboardShell";
import {
  getAdminOverview,
  updateBetaApplicationStatus,
  setUserAdmin,
  setCompanyPlan,
  setUserBanned,
  setCompanyPublic,
} from "@/lib/admin.functions";
import { useAdminMode } from "@/hooks/use-admin-mode";
import { AdminSupport } from "@/components/admin/AdminSupport";
import { AdminUserDetail } from "@/components/admin/AdminUserDetail";
import { AdminPayouts } from "@/components/admin/AdminPayouts";
import { useHydratedTranslation } from "@/components/marketing/Layout";

import { buildSeo } from "@/lib/seo";

type TFn = ReturnType<typeof useHydratedTranslation>;

export const Route = createFileRoute("/_authenticated/admin")({
  head: () =>
    buildSeo({
      path: "/admin",
      title: "Admin — Reality Twin",
      description:
        "Internal administration console for Reality Twin — manage users, workspaces, plans and platform-wide operations.",
      noindex: true,
    }),
  component: AdminDashboard,
});

type AdminTab = "overview" | "users" | "workspaces" | "requests" | "payouts" | "support";
const PLANS = ["free", "starter", "professional", "scale"] as const;
const PLAN_LABEL: Record<string, string> = {
  free: "Free",
  starter: "Starter",
  professional: "Pro",
  scale: "Scale",
};

function relTime(iso: string | null, t: TFn): string {
  if (!iso) return "—";
  const diff = Date.now() - new Date(iso).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1) return t("app.pages.admin.justNow");
  if (m < 60) return t("app.pages.admin.minAgo", { n: m });
  const h = Math.floor(m / 60);
  if (h < 24) return t("app.pages.admin.hourAgo", { n: h });
  const d = Math.floor(h / 24);
  if (d < 30) return t("app.pages.admin.dayAgo", { n: d });
  return new Date(iso).toLocaleDateString();
}

function AdminDashboard() {
  const t = useHydratedTranslation();
  const navigate = useNavigate();
  const fetchOverview = useServerFn(getAdminOverview);
  const updateApp = useServerFn(updateBetaApplicationStatus);
  const toggleAdmin = useServerFn(setUserAdmin);
  const changePlan = useServerFn(setCompanyPlan);
  const banUser = useServerFn(setUserBanned);
  const togglePublic = useServerFn(setCompanyPublic);
  const { setViewAsUser } = useAdminMode();
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useState<AdminTab>("overview");
  const [query, setQuery] = useState("");
  // Which account's record is open. The list stays mounted underneath, so
  // closing the record returns the admin exactly where they were.
  const [openUser, setOpenUser] = useState<string | null>(null);

  const load = useCallback(() => {
    setLoading(true);
    fetchOverview()
      .then((d) => {
        setData(d);
        setError(null);
      })
      .catch((e) => {
        if (String(e).includes("Forbidden")) {
          navigate({ to: "/dashboard", replace: true });
          return;
        }
        setError(String(e?.message ?? e));
      })
      .finally(() => setLoading(false));
  }, [fetchOverview, navigate]);

  useEffect(() => {
    load();
  }, [load]);

  const enterUserMode = () => {
    setViewAsUser(true);
    toast.success(t("app.pages.admin.switchedUserView"));
    navigate({ to: "/dashboard" });
  };

  const handleStatus = async (id: string, status: "approved" | "rejected" | "pending") => {
    try {
      await updateApp({ data: { applicationId: id, status } });
      toast.success(t(`app.pages.admin.ws_${status}`));
      load();
    } catch (e: any) {
      toast.error(e?.message ?? t("app.pages.admin.failed"));
    }
  };

  const handleAdminToggle = async (userId: string, admin: boolean) => {
    try {
      await toggleAdmin({ data: { userId, admin } });
      toast.success(admin ? t("app.pages.admin.grantedAdmin") : t("app.pages.admin.revokedAdmin"));
      load();
    } catch (e: any) {
      toast.error(e?.message ?? t("app.pages.admin.failed"));
    }
  };

  const handlePlan = async (companyId: string, plan: (typeof PLANS)[number]) => {
    try {
      await changePlan({ data: { companyId, plan } });
      toast.success(t("app.pages.admin.planSet", { plan: PLAN_LABEL[plan] }));
      load();
    } catch (e: any) {
      toast.error(e?.message ?? t("app.pages.admin.failed"));
    }
  };

  const handleBan = async (userId: string, banned: boolean) => {
    try {
      await banUser({ data: { userId, banned } });
      toast.success(
        banned ? t("app.pages.admin.userSuspended") : t("app.pages.admin.userReactivated"),
      );
      load();
    } catch (e: any) {
      toast.error(e?.message ?? t("app.pages.admin.failed"));
    }
  };

  const handlePublic = async (companyId: string, isPublic: boolean) => {
    try {
      await togglePublic({ data: { companyId, isPublic } });
      toast.success(
        isPublic ? t("app.pages.admin.workspacePublished") : t("app.pages.admin.workspaceHidden"),
      );
      load();
    } catch (e: any) {
      toast.error(e?.message ?? t("app.pages.admin.failed"));
    }
  };

  // Build a unified user list from auth metadata joined with company + role.
  const adminIds = useMemo(
    () =>
      new Set(
        ((data?.roles as any[]) ?? []).filter((r) => r.role === "admin").map((r) => r.user_id),
      ),
    [data],
  );
  // Group companies by owner — a user may own several twins.
  const companiesByUser = useMemo(() => {
    const m: Record<string, any[]> = {};
    for (const c of (data?.companies as any[]) ?? []) (m[c.user_id] ??= []).push(c);
    return m;
  }, [data]);
  const users = useMemo(() => {
    const meta = (data?.usersMeta as Record<string, any>) ?? {};
    const ownerPlans = (data?.ownerPlans as Record<string, string>) ?? {};
    const consents = (data?.termsAcceptances as Record<string, any>) ?? {};
    return Object.entries(meta)
      .map(([uid, u]) => {
        const twins = companiesByUser[uid] ?? [];
        return {
          userId: uid,
          email: u.email || uid.slice(0, 8),
          joined: u.created_at,
          lastActive: u.last_sign_in_at,
          banned: u.banned,
          isAdmin: adminIds.has(uid),
          // Proof of consent: accounts created before this was introduced
          // legitimately have none, so the UI distinguishes "no record" from
          // "declined" rather than showing a red flag for historic users.
          consent: consents[uid] ?? null,
          twins,
          twinCount: twins.length,
          // Account-level plan (best across the user's twins), not per-twin.
          plan: ownerPlans[uid] ?? (twins.length ? "free" : null),
        };
      })
      .sort((a, b) => (b.joined ?? "").localeCompare(a.joined ?? ""));
  }, [data, adminIds, companiesByUser]);

  const filteredUsers = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return users;
    return users.filter(
      (u) =>
        u.email.toLowerCase().includes(q) ||
        u.twins.some((tw: any) => (tw.name ?? "").toLowerCase().includes(q)),
    );
  }, [users, query]);

  const filteredCompanies = useMemo(() => {
    const q = query.trim().toLowerCase();
    const list = ((data?.companies as any[]) ?? []).map((c) => ({
      ...c,
      ownerEmail: data?.emails?.[c.user_id] ?? c.user_id.slice(0, 8),
    }));
    if (!q) return list;
    return list.filter(
      (c) => c.name.toLowerCase().includes(q) || c.ownerEmail.toLowerCase().includes(q),
    );
  }, [data, query]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-muted-foreground">
        {t("app.pages.admin.loadingConsole")}
      </div>
    );
  }
  if (error) {
    return (
      <DashboardShell
        title={t("app.pages.admin.consoleTitle")}
        subtitle={t("app.pages.admin.errorSub")}
      >
        <div className="rounded-2xl border border-destructive/30 bg-destructive/5 p-6 text-sm">
          {error}
        </div>
      </DashboardShell>
    );
  }

  const s = data.stats;
  const pc = data.planCounts ?? { free: 0, starter: 0, professional: 0, scale: 0 };

  const statCards = [
    { label: t("app.pages.admin.statUsers"), value: s.users ?? users.length, icon: Users },
    { label: t("app.pages.admin.statWorkspaces"), value: s.companies, icon: Building2 },
    {
      label: t("app.pages.admin.statMrr"),
      value: `CHF ${(s.mrr ?? 0).toLocaleString()}`,
      icon: DollarSign,
    },
    { label: t("app.pages.admin.statNewWeek"), value: s.newThisWeek ?? 0, icon: TrendingUp },
    { label: t("app.pages.admin.statPublic"), value: s.publicTwins ?? 0, icon: Globe },
    { label: t("app.pages.admin.statAdmins"), value: s.admins, icon: Shield },
    { label: t("app.pages.admin.statSuspended"), value: s.suspended ?? 0, icon: Ban },
    { label: t("app.pages.admin.statConversations"), value: s.conversations, icon: MessageSquare },
  ];

  const TABS: { id: AdminTab; label: string; icon: typeof Users; count?: number }[] = [
    { id: "overview", label: t("app.pages.admin.tabOverview"), icon: TrendingUp },
    { id: "users", label: t("app.pages.admin.tabUsers"), icon: Users, count: users.length },
    {
      id: "workspaces",
      label: t("app.pages.admin.tabWorkspaces"),
      icon: Building2,
      count: s.companies,
    },
    { id: "requests", label: t("app.pages.admin.tabRequests"), icon: Rocket, count: s.pending },
    {
      id: "payouts",
      label: t("app.pages.admin.tabPayouts", { defaultValue: "Payouts" }),
      icon: Banknote,
    },
    { id: "support", label: t("app.pages.admin.tabSupport"), icon: LifeBuoy },
  ];

  return (
    <DashboardShell
      title={t("app.pages.admin.consoleTitle")}
      subtitle={t("app.pages.admin.consoleSubtitle")}
      actions={
        <button
          onClick={enterUserMode}
          className="inline-flex items-center gap-2 rounded-full border border-border bg-card px-4 py-2 text-sm font-medium hover:bg-cream transition"
        >
          <Sparkles className="h-4 w-4" /> {t("app.pages.admin.actAsUser")}
        </button>
      }
    >
      {/* Tab bar */}
      <div className="flex gap-1.5 flex-wrap border-b border-border pb-4 mb-6">
        {TABS.map((tt) => {
          const Icon = tt.icon;
          const active = tab === tt.id;
          return (
            <button
              key={tt.id}
              onClick={() => setTab(tt.id)}
              className={`inline-flex items-center gap-2 text-sm px-4 py-2 rounded-xl font-medium transition ${
                active
                  ? "bg-foreground text-background shadow-sm"
                  : "border border-transparent text-muted-foreground hover:text-foreground hover:bg-cream"
              }`}
            >
              <Icon className="h-4 w-4" strokeWidth={1.75} />
              {tt.label}
              {tt.count != null && tt.count > 0 && (
                <span
                  className={`text-[11px] px-1.5 py-0.5 rounded-full ${active ? "bg-background/20" : "bg-border/60"}`}
                >
                  {tt.count}
                </span>
              )}
            </button>
          );
        })}
        <button
          onClick={load}
          className="ml-auto text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1 self-center"
        >
          <RotateCcw className="h-3 w-3" /> {t("app.pages.admin.refresh")}
        </button>
      </div>

      {(tab === "users" || tab === "workspaces") && (
        <div className="relative mb-4 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={
              tab === "users"
                ? t("app.pages.admin.searchUsers")
                : t("app.pages.admin.searchWorkspaces")
            }
            className="w-full rounded-full border border-border bg-card pl-9 pr-4 py-2 text-sm focus:outline-none focus:border-foreground/40"
          />
        </div>
      )}

      {tab === "overview" && (
        <div className="space-y-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {statCards.map((st) => (
              <div key={st.label} className="rounded-2xl border border-border bg-card p-5">
                <st.icon className="h-4 w-4 text-muted-foreground" />
                <div className="mt-3 font-serif text-3xl">{st.value}</div>
                <div className="text-xs text-muted-foreground mt-1">{st.label}</div>
              </div>
            ))}
          </div>

          <section>
            <h2 className="font-serif text-2xl mb-3">{t("app.pages.admin.planDistribution")}</h2>
            <p className="text-xs text-muted-foreground -mt-2 mb-3">
              {t("app.pages.admin.planDistSub")}
            </p>
            <div className="rounded-2xl border border-border bg-card p-6 space-y-3">
              {PLANS.map((p) => {
                const count = pc[p] ?? 0;
                const totalAccounts = PLANS.reduce((sum, pl) => sum + (pc[pl] ?? 0), 0);
                const pct = totalAccounts ? Math.round((count / totalAccounts) * 100) : 0;
                return (
                  <div key={p} className="flex items-center gap-4">
                    <div className="w-24 text-sm font-medium">{PLAN_LABEL[p]}</div>
                    <div className="flex-1 h-2.5 rounded-full bg-cream overflow-hidden">
                      <div
                        className="h-full rounded-full bg-foreground"
                        style={{ width: `${pct}%` }}
                      />
                    </div>
                    <div className="w-16 text-right text-sm text-muted-foreground tabular-nums">
                      {count} · {pct}%
                    </div>
                  </div>
                );
              })}
            </div>
          </section>
        </div>
      )}

      {tab === "users" && (
        <div className="rounded-2xl border border-border bg-card overflow-x-auto">
          <table className="w-full text-sm min-w-[720px]">
            <thead className="bg-cream text-xs uppercase tracking-wider text-muted-foreground">
              <tr>
                <th className="text-left px-4 py-3">{t("app.pages.admin.thUser")}</th>
                <th className="text-left px-4 py-3">{t("app.pages.admin.thWorkspace")}</th>
                <th className="text-left px-4 py-3">{t("app.pages.admin.thPlan")}</th>
                <th className="text-left px-4 py-3">{t("app.pages.admin.thJoined")}</th>
                <th className="text-left px-4 py-3">
                  {t("app.pages.admin.thConsent", { defaultValue: "Terms" })}
                </th>
                <th className="text-left px-4 py-3">{t("app.pages.admin.thLastActive")}</th>
                <th className="text-left px-4 py-3">{t("app.pages.admin.thStatus")}</th>
                <th className="text-right px-4 py-3">{t("app.pages.admin.thActions")}</th>
              </tr>
            </thead>
            <tbody>
              {filteredUsers.length === 0 && (
                <tr>
                  <td colSpan={7} className="px-4 py-8 text-center text-muted-foreground">
                    {t("app.pages.admin.noUsers")}
                  </td>
                </tr>
              )}
              {filteredUsers.map((u) => (
                <tr
                  key={u.userId}
                  onClick={() => setOpenUser(u.userId)}
                  className="border-t border-border cursor-pointer hover:bg-cream/60 transition"
                >
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1.5 font-medium">
                      {u.isAdmin && <Crown className="h-3.5 w-3.5 text-[oklch(0.75_0.15_85)]" />}
                      {u.email}
                    </div>
                    <div className="text-[11px] text-muted-foreground">
                      {t("app.pages.admin.dOpenHint", { defaultValue: "Click to open record" })}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-muted-foreground">
                    {u.twinCount === 0 ? (
                      "—"
                    ) : u.twinCount === 1 ? (
                      u.twins[0].name
                    ) : (
                      <span>
                        {u.twins[0].name}{" "}
                        <span className="text-xs text-muted-foreground/70">
                          {t("app.pages.admin.moreTwins", { n: u.twinCount - 1 })}
                        </span>
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3">{u.plan ? <PlanBadge plan={u.plan} /> : "—"}</td>
                  <td className="px-4 py-3 text-muted-foreground text-xs">
                    {relTime(u.joined, t)}
                  </td>
                  <td className="px-4 py-3 text-xs">
                    {u.consent ? (
                      <span
                        className="inline-flex items-center gap-1 rounded-full border border-emerald-500/30 bg-emerald-500/10 text-emerald-700 px-2.5 py-0.5"
                        title={`${new Date(u.consent.at).toLocaleString()} · v${u.consent.version}`}
                      >
                        <CheckCircle2 className="h-3 w-3" />
                        {t("app.pages.admin.consentYes", { defaultValue: "Accepted" })}
                      </span>
                    ) : (
                      <span
                        className="text-muted-foreground/70"
                        title={t("app.pages.admin.consentNoneHint", {
                          defaultValue:
                            "No record — this account was created before consent was captured.",
                        })}
                      >
                        {t("app.pages.admin.consentNone", { defaultValue: "no record" })}
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-muted-foreground text-xs">
                    {relTime(u.lastActive, t)}
                  </td>
                  <td className="px-4 py-3">
                    {u.banned ? (
                      <span className="inline-flex items-center gap-1 rounded-full border border-red-500/30 bg-red-500/10 text-red-700 px-2.5 py-0.5 text-xs">
                        <Ban className="h-3 w-3" /> {t("app.pages.admin.suspended")}
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 rounded-full border border-emerald-500/30 bg-emerald-500/10 text-emerald-700 px-2.5 py-0.5 text-xs">
                        <UserCheck className="h-3 w-3" /> {t("app.pages.admin.active")}
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                    <div className="flex gap-2 justify-end">
                      <button
                        onClick={() => handleAdminToggle(u.userId, !u.isAdmin)}
                        className={`text-xs rounded-full px-3 py-1.5 border ${u.isAdmin ? "bg-foreground text-background border-foreground" : "border-border hover:bg-cream"}`}
                      >
                        {u.isAdmin
                          ? t("app.pages.admin.revokeAdmin")
                          : t("app.pages.admin.makeAdmin")}
                      </button>
                      <button
                        onClick={() => handleBan(u.userId, !u.banned)}
                        className={`text-xs rounded-full px-3 py-1.5 border ${u.banned ? "border-emerald-500/40 text-emerald-700 hover:bg-emerald-500/5" : "border-red-500/40 text-red-600 hover:bg-red-500/5"}`}
                      >
                        {u.banned ? t("app.pages.admin.reactivate") : t("app.pages.admin.suspend")}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === "workspaces" && (
        <div className="rounded-2xl border border-border bg-card overflow-x-auto">
          <table className="w-full text-sm min-w-[760px]">
            <thead className="bg-cream text-xs uppercase tracking-wider text-muted-foreground">
              <tr>
                <th className="text-left px-4 py-3">{t("app.pages.admin.thCompany")}</th>
                <th className="text-left px-4 py-3">{t("app.pages.admin.thOwner")}</th>
                <th className="text-left px-4 py-3">{t("app.pages.admin.thPlan")}</th>
                <th className="text-left px-4 py-3">{t("app.pages.admin.thAiScore")}</th>
                <th className="text-left px-4 py-3">{t("app.pages.admin.thVisibility")}</th>
                <th className="text-left px-4 py-3">{t("app.pages.admin.thCreated")}</th>
              </tr>
            </thead>
            <tbody>
              {filteredCompanies.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-4 py-8 text-center text-muted-foreground">
                    {t("app.pages.admin.noWorkspaces")}
                  </td>
                </tr>
              )}
              {filteredCompanies.map((c) => (
                <tr
                  key={c.id}
                  onClick={() => setOpenUser(c.user_id)}
                  className="border-t border-border cursor-pointer hover:bg-cream/60 transition"
                >
                  <td className="px-4 py-3">
                    <div className="font-medium">{c.name}</div>
                    <div className="text-xs text-muted-foreground">
                      {[c.industry, c.country].filter(Boolean).join(" · ") || "—"}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-muted-foreground">{c.ownerEmail}</td>
                  <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                    <select
                      value={c.plan ?? "free"}
                      onChange={(e) => handlePlan(c.id, e.target.value as any)}
                      className="rounded-full border border-border bg-background px-3 py-1.5 text-xs focus:outline-none focus:border-foreground/40"
                    >
                      {PLANS.map((p) => (
                        <option key={p} value={p}>
                          {PLAN_LABEL[p]}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td className="px-4 py-3">{c.ai_score ?? "—"}</td>
                  <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => handlePublic(c.id, !c.is_public)}
                        className={`inline-flex items-center gap-1 text-xs rounded-full px-2.5 py-1 border ${c.is_public ? "border-emerald-500/40 text-emerald-700 bg-emerald-500/5" : "border-border text-muted-foreground hover:bg-cream"}`}
                        title={
                          c.is_public
                            ? t("app.pages.admin.publicTitle")
                            : t("app.pages.admin.hiddenTitle")
                        }
                      >
                        <Globe className="h-3 w-3" />{" "}
                        {c.is_public
                          ? t("app.pages.admin.publicLabel")
                          : t("app.pages.admin.hiddenLabel")}
                      </button>
                      {c.offers_services && (
                        <span
                          className="inline-flex items-center gap-1 text-xs text-muted-foreground"
                          title={t("app.pages.admin.offersServices")}
                        >
                          <Wrench className="h-3 w-3" />
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-muted-foreground text-xs">
                    {new Date(c.created_at).toLocaleDateString()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === "requests" && (
        <div className="rounded-2xl border border-border bg-card overflow-x-auto">
          <table className="w-full text-sm min-w-[720px]">
            <thead className="bg-cream text-xs uppercase tracking-wider text-muted-foreground">
              <tr>
                <th className="text-left px-4 py-3">{t("app.pages.admin.thUser")}</th>
                <th className="text-left px-4 py-3">{t("app.pages.admin.thUsersExpected")}</th>
                <th className="text-left px-4 py-3">{t("app.pages.admin.thMotivation")}</th>
                <th className="text-left px-4 py-3">{t("app.pages.admin.thStatus")}</th>
                <th className="text-right px-4 py-3">{t("app.pages.admin.thActions")}</th>
              </tr>
            </thead>
            <tbody>
              {(data.applications as any[]).length === 0 && (
                <tr>
                  <td colSpan={5} className="px-4 py-8 text-center text-muted-foreground">
                    {t("app.pages.admin.noRequests")}
                  </td>
                </tr>
              )}
              {(data.applications as any[]).map((a) => (
                <tr key={a.id} className="border-t border-border align-top">
                  <td className="px-4 py-3">
                    <div className="font-medium">
                      {data.emails[a.user_id] ?? a.user_id.slice(0, 8)}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {new Date(a.created_at).toLocaleString()}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-muted-foreground">{a.expected_users}</td>
                  <td className="px-4 py-3 max-w-md">
                    <div className="line-clamp-3 text-muted-foreground">{a.motivation}</div>
                  </td>
                  <td className="px-4 py-3">
                    <StatusBadge status={a.status} />
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex gap-2 justify-end">
                      <button
                        onClick={() => handleStatus(a.id, "approved")}
                        disabled={a.status === "approved"}
                        className="rounded-full bg-foreground text-background text-xs px-3 py-1.5 disabled:opacity-40"
                      >
                        {t("app.pages.admin.approve")}
                      </button>
                      <button
                        onClick={() => handleStatus(a.id, "rejected")}
                        disabled={a.status === "rejected"}
                        className="rounded-full border border-border text-xs px-3 py-1.5 disabled:opacity-40"
                      >
                        {t("app.pages.admin.reject")}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === "payouts" && <AdminPayouts />}

      {tab === "support" && <AdminSupport />}

      {openUser && (
        <AdminUserDetail userId={openUser} onClose={() => setOpenUser(null)} onChanged={load} />
      )}
    </DashboardShell>
  );
}

function PlanBadge({ plan }: { plan: string }) {
  const map: Record<string, string> = {
    free: "border-border text-muted-foreground",
    starter: "border-sky-500/30 bg-sky-500/10 text-sky-700",
    professional:
      "border-[oklch(0.68_0.19_290_/_0.3)] bg-[oklch(0.68_0.19_290_/_0.1)] text-[oklch(0.5_0.2_290)]",
    scale:
      "border-[oklch(0.75_0.15_85_/_0.4)] bg-[oklch(0.75_0.15_85_/_0.12)] text-[oklch(0.5_0.13_85)]",
  };
  return (
    <span
      className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-medium ${map[plan] ?? map.free}`}
    >
      {PLAN_LABEL[plan] ?? plan}
    </span>
  );
}

function StatusBadge({ status }: { status: string }) {
  const t = useHydratedTranslation();
  const map: Record<string, string> = {
    pending: "bg-amber-500/10 text-amber-700 border-amber-500/30",
    approved: "bg-emerald-500/10 text-emerald-700 border-emerald-500/30",
    rejected: "bg-red-500/10 text-red-700 border-red-500/30",
  };
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-xs ${map[status] ?? "border-border"}`}
    >
      {t(`app.pages.admin.st_${status}`, { defaultValue: status })}
    </span>
  );
}
